import { updateSourcingNode } from "@/apis/requirementManagement.api";
import { PrimaryOutlineButton } from "@/components/ui/buttons/PrimaryButton";
import CommonModal from "@/components/ui/CommonModal";
import type { UpdateScourcingNodeSchema } from "@/types/apis";
import { convertFileItems } from "@/utils/file";
import { notifySuccess } from "@/utils/notification";
import {
  DownOutlined,
  ExclamationCircleFilled,
  UpOutlined,
} from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UploadFile } from "antd";
import { Button, Form, Space, Tag, Timeline } from "antd";
import { useEffect } from "react";
import { useSourcingFlowNodesFormBehavior } from "../../hooks/useSourcingFlowNodesFormBehavior";
import type { SourcingNodeConfig } from "../../type";
import { NodeForm } from "./NodeForm/NodeForm";
import styles from "./SouringFollowModal.module.less";
import clsx from "clsx";
import { useGlobalModal } from "@/hooks/context/ModalContext";
import { useModal } from "@/hooks/modals/useModal";

type Props = {
  currentSourcingMaterialId: string;
  open: boolean;
  onClose: () => void;
  defaultSourcingNodes: SourcingNodeConfig[];
};

export function SourcingFollowModal({
  currentSourcingMaterialId,
  open,
  onClose,
  defaultSourcingNodes,
}: Props) {
  const {
    sourcingNodes,
    isSourcingBy,
    collapsedKeys,
    expandAll,
    collapseAll,
    toggleCollapse,
  } = useSourcingFlowNodesFormBehavior(
    defaultSourcingNodes,
    currentSourcingMaterialId,
  );

  return (
    <CommonModal
      title="寻源节点跟进"
      open={open}
      centered
      footer={
        <PrimaryOutlineButton onClick={onClose}>关闭</PrimaryOutlineButton>
      }
      width={1100}
      onCancel={onClose}
    >
      <Form disabled={false} className={styles.modalWrapper}>
        <div className={styles.actionBar}>
          <Space>
            {collapsedKeys.size > 0 ? (
              <Button type="link" size="small" onClick={expandAll}>
                全部展开
                <DownOutlined />
              </Button>
            ) : (
              <Button type="link" size="small" onClick={collapseAll}>
                全部折叠
                <UpOutlined />
              </Button>
            )}
          </Space>
        </div>
        <Timeline
          mode="left"
          items={sourcingNodes.map((node, index) => {
            // 找到第一个未完成的节点索引
            const firstIncompleteIndex = sourcingNodes.findIndex(
              (n) => !n.extra?.isCompleted,
            );
            const showCompleteButton =
              !node.extra?.isCompleted && index === firstIncompleteIndex;

            return {
              key: node.nodeCode,
              dot: (
                <div
                  className={clsx([
                    styles.nodeDot,
                    node.extra?.isCompleted && styles.completedNodeDot,
                  ])}
                >
                  {node.nodeCode}
                </div>
              ),
              children: (
                <NodeCard
                  isSourcingBy={isSourcingBy}
                  node={node}
                  collapsed={collapsedKeys.has(node.nodeCode)}
                  onToggle={() => toggleCollapse(node.nodeCode)}
                  showCompleteButton={showCompleteButton}
                  open={open}
                />
              ),
            };
          })}
        />
      </Form>
    </CommonModal>
  );
}

function NodeCard({
  node,
  collapsed,
  onToggle,
  showCompleteButton,
  open,
  isSourcingBy,
}: {
  node: SourcingNodeConfig;
  collapsed: boolean;
  onToggle: () => void;
  showCompleteButton: boolean;
  open: boolean;
  isSourcingBy: boolean;
}) {
  const isEmptyNode = node.fields.length === 0;
  const [form] = Form.useForm();
  const globalModal = useGlobalModal();

  const { props } = useModal("SOURCING_APPLICATION_20251202");
  const { recordId } = props;

  const queryClient = useQueryClient();

  const completeMutation = useMutation({
    mutationFn: (data: UpdateScourcingNodeSchema) => updateSourcingNode(data),
    onSuccess() {
      notifySuccess("操作成功");
      queryClient.invalidateQueries({
        queryKey: ["querySourcingRequirementDetail", recordId],
      });
    },
  });

  // 表单值初始化
  useEffect(() => {
    if (!open) return;
    const extra = node.extra;
    if (!extra) return;

    form.setFieldsValue({
      ...extra,
      relatedFiles: extra.relatedFiles
        ? convertFileItems(extra.relatedFiles)
        : null,
      newRelatedFiles: extra.newRelatedFiles
        ? convertFileItems(extra.newRelatedFiles)
        : null,
    });
  }, [open, node.extra, form]);

  const handleComplete = async () => {
    const values = await form.validateFields();

    globalModal.confirm({
      title: "完成节点",
      icon: <ExclamationCircleFilled />,
      content: "当前节点完成后，将无法取消操作，您确定要继续吗？",
      onOk() {
        if (!node.extra?.id) return;

        completeMutation.mutate({
          id: node.extra?.id,
          nodeCode: node.nodeCode,
          nodeStatus: "2",
          nodeRemarks: values.nodeRemarks || "",
          supplierId: values.supplierId?.join() || "",
          relatedFiles:
            values.relatedFiles?.map((file: UploadFile) => file.url).join() ||
            "",
          newRelatedFiles:
            values.newRelatedFiles
              ?.map((file: UploadFile) => file.url)
              .join() || "",
          relatedForm: values.relatedForm || null,
        });
      },
    });
  };

  return (
    <div className={styles.nodeCard}>
      <div
        className={`${styles.nodeHeader} ${isEmptyNode ? styles.nodeHeaderEmpty : ""}`}
        onClick={isEmptyNode ? undefined : onToggle}
      >
        <span className={styles.nodeTitle}>
          {node.nodeName}
          <Tag
            style={{
              marginLeft: "15px",
              color: node.extra?.isCompleted ? "#009A03" : "#EF5700",
              backgroundColor: node.extra?.isCompleted ? "#CEFFD0" : "#FFE8DB",
              borderColor: node.extra?.isCompleted ? "#CEFFD0" : "#FFE8DB",
            }}
          >
            {node.extra?.isCompleted ? "已完成" : "待完成"}
          </Tag>
        </span>

        {isEmptyNode ? (
          showCompleteButton && (
            <Button
              type="primary"
              size="small"
              onClick={handleComplete}
              disabled={!isSourcingBy || node.extra?.isCompleted === true}
            >
              完成节点
            </Button>
          )
        ) : (
          <Button type="link" size="small">
            {collapsed ? (
              <>
                展开 <DownOutlined />
              </>
            ) : (
              <>
                折叠 <UpOutlined />
              </>
            )}
          </Button>
        )}
      </div>
      {!collapsed && !isEmptyNode && (
        <div className={styles.nodeContent}>
          <div className={styles.nodeFormWrapper}>
            <NodeForm
              disabled={!isSourcingBy || node.extra?.isCompleted === true}
              node={node}
              form={form}
            />
          </div>

          {showCompleteButton && isSourcingBy && (
            <div className={styles.completeButtonWrapper}>
              <Button
                disabled={!isSourcingBy || node.extra?.isCompleted === true}
                type="primary"
                onClick={handleComplete}
                loading={completeMutation.isPending}
              >
                完成节点
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
